<script>
	import { tick } from "svelte";

	import Button, { Label, Icon } from "@smui/button";
	import { Icon as CommonIcon } from "@smui/common";
	import Card, { Content } from "@smui/card";
	import Textfield from "@smui/textfield";
	import TextfieldIcon from "@smui/textfield/icon";
	import Snackbar, { Actions, Label as SnackbarLabel } from "@smui/snackbar";
	import IconButton from "@smui/icon-button";
	import Tab, { Label as TabLabel } from "@smui/tab";
	import TabBar from "@smui/tab-bar";
	import Menu from "@smui/menu";
	import List, { Item, Text } from "@smui/list";
	import Dialog, { Title as DialogTitle, Content as DialogContent, Actions as DialogActions } from "@smui/dialog";

	import EditTranslation from "./EditTranslation.svelte";
	import GeoTranslationRow from "./GeoTranslationRow.svelte";
	import EditOption from "./EditOption.svelte";
	import InternalBadge from "./InternalBadge.svelte";
	import EditFontTheme from "../../../components/EditFontTheme.svelte";
	import EditColorTheme from "../../../components/EditColorTheme.svelte";
	import ButtonSaveFloating from "../../../components/ButtonSaveFloating.svelte";

	import {
		setupComplete,
		embedInfo,
		selectedLanguage,
		selectedLanguageName,
		selectedOrganizationObj,
		organizationProjectSettings,
		colorthemes,
		debug,
    graphics,
	} from "../../../stores";
	import { optionsToObject, translationsToObject, encodeTranslations, isTranslationTooLong, triggerResize } from "../../../utils";
	import api from "../../../api";

	export let graphic;
	export let instance;
	export let translations;

	export let createInstance;
	export let saveInstance;

	export let instanceToDelete;
	export let refreshString;
	export let modified;

	let snackbar;

	$: options = instance.options;
	$: optionsStringified = JSON.stringify(optionsToObject(options));

	$: overwriteFontTheme = instance.overwrite_font_theme;
	$: fontThemeString =
		overwriteFontTheme.selected ||
		$organizationProjectSettings.font_theme.original ||
		$selectedOrganizationObj.font_theme.original;

	$: overwriteColorTheme = instance.overwrite_color_theme;
	$: colorThemeString = overwriteColorTheme.selected || $organizationProjectSettings.color_theme.original;

	$: translationsStringified = encodeTranslations({ ...translationsToObject(translations), ...geoPreviewTranslations });

	$: suggestionsShown = translations.some((t) => t.isSuggestion);
	$: machineTranslationsShown = translations.some((t) => t.isMachineTranslation);
	$: missingTranslations = translations.filter((t) => t.isMissing).length;
	$: suggestedTranslations = translations.filter((t) => t.isSuggestion).length;
	$: hasPlaceholders = translations.some((t) => /\{[^}]+\}/.test(t.string));

	// Split translations into short and long; is_long overrides the length heuristic
	const isLongString = (t) => t.is_long === true || (t.is_long !== false && t.string.length > 50);
	$: shortTranslations = translations.filter((t) => !isLongString(t));
	$: longTranslations = translations.filter((t) => isLongString(t));

	// Handle modified state for long translations
	const updateLongTranslation = (t) => {
		t.modified = (t.input !== t.original && !(t.original === null && t.input === "")) || !!t.isSuggestion;
		t.isMissing = !t.input || t.input === "";
		t.isTooLong = isTranslationTooLong(t.string?.length, t.input?.length);
		// as soon as a suggestion is changed, it's no longer considered a suggestion
		if (t.input !== t.original && t.isSuggestion) t.isSuggestion = false;
	};

	// ///////////////////////////////////////////////////////////
	// place names (geo translations)
	// ///////////////////////////////////////////////////////////
	const GEO_PAGE_SIZE = 50;

	let geoStatus = null;
	let geoRows = [];
	let geoCount = 0;
	let geoPage = 1;
	let geoQuery = "";
	let geoSearchValue = "";
	let geoSearchResults = [];
	let geoSearchOpen = false;
	let geoSearchTimer;
	let geoMachineFilter = null;
	let geoDirtyRows = {};
	let geoSectionEl;
	let geoToolsMenu;
	let geoSnackbar;
	let geoSnackbarText = "";

	const fmtNumber = (n) => (n || 0).toLocaleString("en-US");
	const geoUrl = (suffix = "") => `graphicInstances/${instance.graphicinstance_id}/geoTranslations${suffix}/`;
	const geoOriginal = (row) => row.translation || "";
	const isGeoRowDirty = (row) => row.input !== geoOriginal(row);

	$: geoBufferRows = Object.values(geoDirtyRows);
	$: geoDirty = geoBufferRows.some(isGeoRowDirty);
	$: geoDisplayRows = [
		...geoBufferRows.filter((d) => !geoRows.some((r) => r.string === d.string)),
		...geoRows.map((r) => geoDirtyRows[r.string] || { ...r, input: geoOriginal(r) }),
	];
	$: geoPageCount = Math.ceil(geoCount / GEO_PAGE_SIZE);
	$: geoPreviewTranslations = Object.values(geoDirtyRows)
		.filter(isGeoRowDirty)
		.reduce((obj, r) => ({ ...obj, [r.string]: r.input || r.string }), {});

	// staleness guards: geoEpoch invalidates every in-flight geo response on
	// instance switch; geoRowsSeq/geoSearchSeq make last-issued (not
	// last-arriving) win across quick page/filter/typing changes
	let geoEpoch = 0;
	let geoRowsSeq = 0;
	let geoSearchSeq = 0;

	const fetchGeoStatus = async () => {
		const epoch = geoEpoch;
		const status = await api.get(geoUrl("/status"));
		if (epoch !== geoEpoch) return;
		geoStatus = status;
	};

	const fetchGeoRows = async () => {
		const epoch = geoEpoch;
		const seq = ++geoRowsSeq;
		const params = new URLSearchParams({ mode: "translated", page: geoPage, page_size: GEO_PAGE_SIZE });
		if (geoQuery) params.set("q", geoQuery);
		if (geoMachineFilter !== null) params.set("machine", geoMachineFilter);
		const data = await api.get(`${geoUrl()}?${params.toString()}`);
		if (epoch !== geoEpoch || seq !== geoRowsSeq) return;
		if (data.results.length === 0 && data.count > 0 && geoPage > 1) {
			geoPage = Math.max(1, Math.ceil(data.count / GEO_PAGE_SIZE));
			return fetchGeoRows();
		}
		geoRows = data.results;
		geoCount = data.count;
	};

	const refreshGeo = () => Promise.all([fetchGeoStatus(), fetchGeoRows()]);

	const initGeo = async () => {
		geoEpoch += 1;
		clearTimeout(geoSearchTimer);
		geoStatus = null;
		geoRows = [];
		geoCount = 0;
		geoPage = 1;
		geoQuery = "";
		geoSearchValue = "";
		geoSearchResults = [];
		geoSearchOpen = false;
		geoMachineFilter = null;
		geoDirtyRows = {};
		const epoch = geoEpoch;
		try {
			await fetchGeoStatus();
		} catch (e) {
			if (epoch === geoEpoch) geoStatus = null;
			return;
		}
		if (epoch !== geoEpoch) return;
		if (geoStatus && geoStatus.total > 0) fetchGeoRows();
	};

	let geoInstanceId;
	$: if (instance && instance.graphicinstance_id !== geoInstanceId) {
		geoInstanceId = instance.graphicinstance_id;
		initGeo();
	}

	const onGeoSearchInput = () => {
		clearTimeout(geoSearchTimer);
		geoSearchTimer = setTimeout(applyGeoSearch, 300);
	};

	const applyGeoSearch = () => {
		geoQuery = geoSearchValue.trim();
		geoPage = 1;
		fetchGeoRows();
		fetchGeoSearchResults();
	};

	const fetchGeoSearchResults = async () => {
		const epoch = geoEpoch;
		const seq = ++geoSearchSeq;
		if (!geoQuery) {
			geoSearchResults = [];
			geoSearchOpen = false;
			return;
		}
		const params = new URLSearchParams({ mode: "all", q: geoQuery, page_size: 20 });
		const results = (await api.get(`${geoUrl()}?${params.toString()}`)).results;
		if (epoch !== geoEpoch || seq !== geoSearchSeq) return;
		geoSearchResults = results;
		geoSearchOpen = geoSearchResults.length > 0;
	};

	const onGeoSearchFocusOut = (e) => {
		if (e.currentTarget.contains(e.relatedTarget)) return;
		geoSearchOpen = false;
	};

	const setGeoMachineFilter = (value) => {
		geoMachineFilter = value;
		geoPage = 1;
		fetchGeoRows();
	};

	const setGeoPage = (page) => {
		geoPage = page;
		fetchGeoRows();
	};

	const editGeoRow = (row, value) => {
		geoDirtyRows[row.string] = { ...row, input: value };
		geoDirtyRows = geoDirtyRows;
	};

	const clearGeoRow = (row) => {
		if (row.translation) {
			editGeoRow(row, "");
		} else {
			delete geoDirtyRows[row.string];
			geoDirtyRows = geoDirtyRows;
		}
	};

	const focusGeoRow = async (string) => {
		await tick();
		const row = geoSectionEl?.querySelector(`[data-geo-string="${CSS.escape(string)}"]`);
		if (!row) return;
		row.scrollIntoView({ behavior: "smooth", block: "center" });
		row.querySelector("input")?.focus();
	};

	const addGeoRowFromSearch = (result) => {
		geoSearchOpen = false;
		const alreadyVisible = geoDirtyRows[result.string] || geoRows.some((r) => r.string === result.string);
		if (!alreadyVisible) {
			geoDirtyRows[result.string] = {
				...result,
				input: result.translation || (result.suggestion ? result.suggestion.translation : ""),
			};
			geoDirtyRows = geoDirtyRows;
		}
		focusGeoRow(result.string);
	};

	const openGeoSnackbar = (text) => {
		geoSnackbarText = text;
		geoSnackbar.open();
	};

	const geoSavePayload = () => {
		const rows = Object.values(geoDirtyRows)
			.filter(isGeoRowDirty)
			.map((r) => ({ string: r.string, translation: r.input || "" }));
		return {
			rows,
			onSaved: (savedRows) => onGeoSaved(savedRows, rows),
			onError: () =>
				openGeoSnackbar("Saving place names failed — your edits are still here. Please try again."),
		};
	};

	const onGeoSaved = async (savedRows, sentRows) => {
		const removed = savedRows.filter((r) => r.deleted).length;
		const rejected = savedRows.filter((r) => r.error).length;
		// clear only rows that went out AND are unchanged since the snapshot —
		// edits made while the save was in flight stay dirty
		for (const sent of sentRows) {
			const current = geoDirtyRows[sent.string];
			if (current && (current.input || "") === sent.translation) {
				delete geoDirtyRows[sent.string];
			}
		}
		geoDirtyRows = geoDirtyRows;
		if (removed > 0) {
			openGeoSnackbar(
				`Removed ${removed} place name translation${
					removed === 1 ? "" : "s"
				} — empty entries and entries identical to the original are not stored.`
			);
		}
		if (rejected > 0) {
			openGeoSnackbar(
				`${rejected} place name${rejected === 1 ? " is" : "s are"} no longer part of this graphic and could not be saved.`
			);
		}
		await refreshGeo();
	};

	let copyDialogOpen = false;

	const openCopyDialog = () => {
		if (geoDirty) {
			openGeoSnackbar("Save your changes first.");
			return;
		}
		copyDialogOpen = true;
	};

	const applyGeoSuggestions = async () => {
		if (geoDirty) {
			openGeoSnackbar("Save your changes first.");
			return;
		}
		let res;
		try {
			res = await api.post(geoUrl("/applySuggestions"), {});
		} catch (e) {
			openGeoSnackbar("Copying translations failed. Please try again.");
			return;
		}
		openGeoSnackbar(`Copied ${fmtNumber(res.applied)} translation${res.applied === 1 ? "" : "s"}`);
		await refreshGeo();
	};

	const machineTranslateGeoRow = async (row) => {
		const current = geoDirtyRows[row.string];
		if (current && isGeoRowDirty(current)) {
			openGeoSnackbar(`Save or clear your edit of "${row.string}" first.`);
			return;
		}
		let res;
		try {
			res = await api.post(geoUrl("/machineTranslate"), { string: row.string });
		} catch (e) {
			openGeoSnackbar(`Machine translation failed for "${row.string}". Please try again.`);
			return;
		}
		if (res && res.translation && !res.deleted) {
			delete geoDirtyRows[row.string];
			const index = geoRows.findIndex((r) => r.string === res.string);
			if (index >= 0) geoRows[index] = res;
			else geoDirtyRows[res.string] = { ...res, input: res.translation };
			geoRows = geoRows;
			geoDirtyRows = geoDirtyRows;
			fetchGeoStatus();
		} else if (res && res.disabled) {
			openGeoSnackbar("Machine translation is not available for this language.");
		} else {
			openGeoSnackbar(`DeepL found no different translation for "${row.string}".`);
		}
	};

	// bulk machine translation: client-driven chunk loop with exact progress
	let mtDialogOpen = false;
	let mtState = "confirm";
	let mtAdded = 0;
	let mtUnchanged = 0;
	let mtRemaining = null;
	let mtStop = false;
	let mtRun = 0;
	// cursor keeps successive chunks disjoint: discarded DeepL results stay
	// "missing", so restarting from the top would re-translate them forever
	let mtCursor = null;

	const openMtDialog = () => {
		if (geoDirty) {
			openGeoSnackbar("Save your changes first.");
			return;
		}
		mtRun += 1;
		mtState = "confirm";
		mtAdded = 0;
		mtUnchanged = 0;
		mtRemaining = null;
		mtStop = false;
		mtCursor = null;
		mtDialogOpen = true;
	};

	const runMtLoop = async () => {
		const run = ++mtRun;
		mtState = "running";
		while (true) {
			let res;
			try {
				const body = mtCursor === null ? { chunk: 100 } : { chunk: 100, cursor: mtCursor };
				res = await api.post(geoUrl("/machineTranslate"), body);
			} catch (e) {
				if (run !== mtRun) return;
				if (mtStop) {
					refreshGeo();
					return;
				}
				mtState = "error";
				return;
			}
			if (run !== mtRun) return;
			mtAdded += res.added;
			mtUnchanged += res.processed - res.added;
			mtRemaining = res.remaining;
			if (res.cursor !== undefined && res.cursor !== null) mtCursor = res.cursor;
			if (res.disabled) {
				mtState = "disabled";
				return;
			}
			if (res.remaining === 0 || res.processed === 0) {
				mtState = "done";
				mtDialogOpen = false;
				openGeoSnackbar(
					`DeepL added ${fmtNumber(mtAdded)} translation${mtAdded === 1 ? "" : "s"} (${fmtNumber(
						mtUnchanged
					)} name${mtUnchanged === 1 ? "" : "s"} unchanged)`
				);
				refreshGeo();
				return;
			}
			if (mtStop) {
				refreshGeo();
				return;
			}
		}
	};

	const onMtDialogClosed = () => {
		mtStop = true;
		// a running loop notices the stop after its in-flight chunk and refetches itself
		if ((mtState === "error" || mtState === "disabled") && (mtAdded > 0 || mtUnchanged > 0)) refreshGeo();
	};

	$: if ($debug) console.log("INSTANCE", instance);
	// $: if ($debug) console.log("TRANSLATIONS", translations);
	// $: if ($debug) console.log ("OPTIONS", options);
	// $: if ($debug) console.log ("MODIFIED", modified);

	// if setup complete and graphic or instance changes, trigger resize
	$: if ($setupComplete)
		graphic, instance, options, translations, overwriteFontTheme, overwriteColorTheme, triggerResize();

	// ///////////////////////////////////////////////////////////
	// create iframe URL with all necessary parameters
	// ///////////////////////////////////////////////////////////
	$: iframeUrl = `${instance.embed_url_edit}${instance.embed_url_edit.includes('?') ? '&' : '?'}v=${refreshString}&force_color_scheme=${colorScheme}&options=${encodeURIComponent(optionsStringified)}&fonttheme=${encodeURIComponent(fontThemeString)}&colortheme=${encodeURIComponent(colorThemeString)}&translations=${translationsStringified}${fixedHeight ? '&fixed_height=1' : ''}`;

	const addAdditionalUrlParameter = () => {
		if (modified.heading) iframeUrl += `&heading=${encodeURIComponent(modified.heading)}`;
		if (modified.description) iframeUrl += `&description=${encodeURIComponent(modified.description)}`;
		if (modified.byline) iframeUrl += `&byline=${encodeURIComponent(modified.byline)}`;
	};

	$: if ($setupComplete)
		graphic, instance, options, translations, overwriteFontTheme, overwriteColorTheme, addAdditionalUrlParameter();

	// ///////////////////////////////////////////////////////////
	// react to user input
	// ///////////////////////////////////////////////////////////
	// if a user changes the translations or options, check if they are different from the original
	const checkAll = () => {
		modified.translations = translations.some((t) => t.modified || t.isSuggestion);
		modified.options = options.some((o) => o.modified);
		modified.fonttheme = overwriteFontTheme.modified;
		modified.colortheme = overwriteColorTheme.modified;
		modified.geoTranslations = geoDirty;
	};
	$: translations, options, overwriteFontTheme, overwriteColorTheme, geoDirty, checkAll();


	// Handle embed codes array
	
	$: embedCodes = instance.embed_codes || [];
	$: hasEmbedCodes = embedCodes.length > 0;
	$: hasMultipleEmbedCodes = embedCodes.length > 1;
	
	const copyEmbedCode = () => {
		const currentEmbedCode = getCurrentEmbedCode();
		if (currentEmbedCode) {
			navigator.clipboard.writeText(currentEmbedCode);
			snackbar.open();
		}
	};

	let colorScheme = 'light';
	let selectedEmbedTab = 0;

	// Fixed-height embed: only offered for graphics that declare support. When on,
	// the copied snippet is the fixed-height variant (no resize script) and the
	// preview is driven at a fixed height via ?fixed_height=1.
	// The toggle shows only when the graphic supports fixed height AND the selected
	// organisation is opted in (per-org flag, default off).
	$: supportsFixedHeight =
		!graphic.internal_only &&
		!!graphic.supports_fixed_height &&
		!!$selectedOrganizationObj?.fixed_height_enabled;
	let fixedHeight = false;
	// Height the preview iframe is pinned to while previewing fixed-height mode
	// (the partner picks their own on the real embed). Editable so the journalist
	// can check tall graphics like the small multiples.
	let fixedHeightPreviewPx = 600;
	// never leave the toggle stuck on for a graphic that can't honor it
	$: if (!supportsFixedHeight && fixedHeight) fixedHeight = false;

	// Get current embed code based on selection
	const getCurrentEmbedCode = () => {
		if (!embedCodes || embedCodes.length === 0) return '';
		let entry;
		if (embedCodes.length === 1) {
			entry = embedCodes[0];
		} else {
			entry = embedCodes.find((code) => code.target === selectedEmbedTab);
			if (!entry) {
				selectedEmbedTab = embedCodes[0].target;
				entry = embedCodes[0];
			}
		}
		if (fixedHeight && entry.code_fixed_height) return entry.code_fixed_height;
		return entry.code || '';
	};

	$: currentEmbedCode = getCurrentEmbedCode(embedCodes, selectedEmbedTab, fixedHeight);

	// oEmbed orgs: show a copyable per-instance URL (supported push targets only)
	// instead of the iframe snippet.
	$: oembedMode = instance.oembed_mode;
	$: oembedUrls = instance.oembed_urls || [];
	$: hasOembedUrls = oembedUrls.length > 0;
	$: hasMultipleOembedUrls = oembedUrls.length > 1;

	let selectedOembedTab = null;
	// reset the selected tab to a real target name whenever the list changes
	// (avoids the numeric-vs-name pitfall of selectedEmbedTab)
	$: if (oembedUrls) selectedOembedTab = oembedUrls[0]?.target ?? null;

	const getCurrentOembedUrl = () => {
		if (!oembedUrls.length) return '';
		if (oembedUrls.length === 1) return oembedUrls[0].url;
		return oembedUrls.find((u) => u.target === selectedOembedTab)?.url || oembedUrls[0].url;
	};

	$: currentOembedUrl = getCurrentOembedUrl(oembedUrls, selectedOembedTab);

	const copyOembedUrl = () => {
		const url = getCurrentOembedUrl();
		if (url) {
			navigator.clipboard.writeText(url);
			snackbar.open();
		}
	};
</script>

<main>
	{#if graphic}
		<div class="container graphic-instance">
      <h4>
        {graphic.meta_title}
        {#if graphic.internal_only}
          <InternalBadge />
        {/if}
      </h4>
      
      {#if graphic.meta_description}
        <div class="description">
          {@html graphic.meta_description}
        </div>
      {/if}
			<Card style="background-color: {colorScheme == "dark" ? ($selectedOrganizationObj.color_background_dark || "#1a1a1a") : ($selectedOrganizationObj.color_background || "#ffffff")}">
				<iframe
					class={fixedHeight ? '' : $embedInfo.resize_iframe_class}
					src={iframeUrl}
					frameborder="0"
					scrolling="no"
					title=""
					style={fixedHeight ? `height:${fixedHeightPreviewPx}px` : ''}
					on:load={() => {
						if (!fixedHeight) triggerResize();
					}}
				/>
			</Card>

			{#if instance.darkmode_enabled}
				<div class="darkmode-toggle">
					<span>Preview</span>
					<Button on:click={() => (colorScheme = 'light')} color="primary" variant={colorScheme === 'light' ? 'unelevated' : 'outlined'}>
						Light
					</Button>
					<Button on:click={() => (colorScheme = 'dark')} color="primary" variant={colorScheme === 'dark' ? 'unelevated' : 'outlined'}>
						Dark
					</Button>
				</div>
			{/if}
			

			{#if options.length > 0}
				<h5>Options</h5>
				<div class="options-container">
					{#key instance.graphicinstance_id}
						{#each options as o}
							<EditOption bind:selected={o.selected} bind:modified={o.modified} {...o} />
						{/each}
					{/key}
				</div>
			{/if}
			{#if translations.length > 0}
				<h5>
					Translations to {$selectedLanguageName}
				</h5>
				<div class="translation-instructions">
					<p>
						<CommonIcon class="material-icons icon">info</CommonIcon>
						Check the preview above to see how your translations look. Translations should generally not be much longer than the original text.
					</p>
					{#if hasPlaceholders}
						<p>Some texts contain placeholders like <code>{'{example}'}</code>. Keep these exactly as they are in your translation.</p>
					{/if}
				</div>
				<div class="translation-info">
					{#if missingTranslations}
						<span class="missing">
							{missingTranslations} translation{missingTranslations === 1 ? "" : "s"} missing
							<CommonIcon class="material-icons icon">sentiment_very_dissatisfied</CommonIcon>
						</span>
					{/if}
					{#if missingTranslations && suggestedTranslations}
						<span>, </span>
					{/if}
					{#if suggestedTranslations}
						<span class="suggested">
							{suggestedTranslations} suggestion{suggestedTranslations === 1 ? "" : "s"} need{suggestedTranslations ===
							1
								? "s"
								: ""} attention
							<CommonIcon class="material-icons icon">sentiment_neutral</CommonIcon>
						</span>
					{/if}
					{#if !missingTranslations && !suggestedTranslations}
						<span class="complete">
							Translations completed
							<CommonIcon class="material-icons icon">sentiment_very_satisfied</CommonIcon>
						</span>
					{/if}
				</div>
				{#if suggestionsShown}
					<div class="suggestion-explainer">
						<CommonIcon class="material-icons icon">info</CommonIcon>
						Orange translations are suggestions. Confirm them with
						<strong><CommonIcon class="material-icons icon">save</CommonIcon> SAVE CHANGES </strong> or insert a different
						translation.
					</div>
				{/if}
				{#if machineTranslationsShown}
					<div class="machine-translation-explainer">
						<CommonIcon class="material-icons icon">smart_toy</CommonIcon>
						Translated by a machine.
					</div>
				{/if}
				<p />
				{#if longTranslations.length > 0}
					<h6 class="translations-section-heading">Longer translations</h6>
					<div class="long-translations-container">
						{#each longTranslations as t (t.string)}
							<div class="long-translation" class:suggestion={t.isSuggestion} class:machine={t.isMachineTranslation} class:missing={t.isMissing} class:too-long={t.isTooLong}>
								<div class="original-string">{t.string}</div>
								<Textfield
									textarea
									variant="filled"
									bind:value={t.input}
									on:input={() => updateLongTranslation(t)}
									style="width: 100%;"
									input$placeholder={t.string}
								/>
								{#if t.isTooLong && !t.isMissing}
									<div class="length-warning">
										Translation is significantly longer than original
									</div>
								{/if}
							</div>
						{/each}
					</div>
				{/if}
				{#if shortTranslations.length > 0}
					{#if longTranslations.length > 0}
						<h6 class="translations-section-heading">Shorter translations</h6>
					{/if}
					<div class="translations-container">
						{#each shortTranslations as t (t.string)}
							<EditTranslation
								bind:input={t.input}
								bind:modified={t.modified}
								bind:isMissing={t.isMissing}
								bind:isSuggestion={t.isSuggestion}
								bind:isMachineTranslation={t.isMachineTranslation}
								{...t}
							/>
						{/each}
					</div>
				{/if}
			{/if}
			{#if geoStatus && geoStatus.total > 0}
				<div class="geo-section" bind:this={geoSectionEl}>
					<h5>Translate place names</h5>
					<div class="translation-instructions">
						<p>
							<CommonIcon class="material-icons icon">info</CommonIcon>
							Country, region and city names appear exactly as in the data. Add a translation only where your language
							uses a different name — most names need none.
						</p>
					</div>
					<div
						class="geo-search"
						on:focusin={() => (geoSearchOpen = geoSearchResults.length > 0)}
						on:focusout={onGeoSearchFocusOut}
					>
						<Textfield
							variant="filled"
							bind:value={geoSearchValue}
							on:input={onGeoSearchInput}
							label="Find and translate place names…"
							style="width: 100%;"
						>
							<TextfieldIcon class="material-icons" slot="leadingIcon">search</TextfieldIcon>
						</Textfield>
						{#if geoSearchOpen && geoSearchResults.length > 0}
							<div class="geo-search-results">
								{#each geoSearchResults as result (result.string)}
									<button
										type="button"
										class="geo-search-result"
										on:mousedown|preventDefault={() => addGeoRowFromSearch(result)}
									>
										<span class="original">{result.string}</span>
										{#if result.translation}
											<span class="current">{result.translation}</span>
										{:else if result.suggestion}
											<span class="hint">suggestion: "{result.suggestion.translation}" (from another graphic)</span>
										{/if}
									</button>
								{/each}
							</div>
						{/if}
					</div>
					<div class="geo-toolbar">
						<div class="geo-count">
							{#if geoStatus.translated > 0}
								<strong>{fmtNumber(geoStatus.translated)} translated name{geoStatus.translated === 1 ? "" : "s"}</strong>
								({fmtNumber(geoStatus.manual)} manual · {fmtNumber(geoStatus.machine)} 🤖)
							{:else}
								No translated names yet — all names appear as in the data. That's usually right.
							{/if}
						</div>
						{#if geoStatus.translated > 0}
							<div class="geo-filter">
								<Button
									on:click={() => setGeoMachineFilter(null)}
									color="primary"
									variant={geoMachineFilter === null ? "unelevated" : "outlined"}
								>
									All
								</Button>
								<Button
									on:click={() => setGeoMachineFilter(false)}
									color="primary"
									variant={geoMachineFilter === false ? "unelevated" : "outlined"}
								>
									Manual
								</Button>
								<Button
									on:click={() => setGeoMachineFilter(true)}
									color="primary"
									variant={geoMachineFilter === true ? "unelevated" : "outlined"}
								>
									🤖
								</Button>
							</div>
						{/if}
						<div class="geo-tools">
							<IconButton class="material-icons" title="Tools" on:click={() => geoToolsMenu.setOpen(true)}>
								more_vert
							</IconButton>
							<Menu bind:this={geoToolsMenu} anchorCorner="BOTTOM_LEFT">
								<!-- SMUI Item freezes `disabled` at mount; re-key on the guard state -->
								{#key `${geoDirty}|${geoStatus.suggestions_available}`}
									<List>
										<Item
											disabled={geoDirty || !geoStatus.suggestions_available}
											on:SMUI:action={openCopyDialog}
										>
											<Text>Copy translations from other graphics ({fmtNumber(geoStatus.suggestions_available)})</Text>
										</Item>
										<Item disabled={geoDirty} on:SMUI:action={openMtDialog}>
											<Text>Translate ALL with DeepL…</Text>
										</Item>
									</List>
								{/key}
								{#if geoDirty}
									<p class="geo-tools-hint">Save your changes first</p>
								{/if}
							</Menu>
						</div>
					</div>
					{#if geoDisplayRows.length > 0}
						<div class="geo-rows">
							{#each geoDisplayRows as row (row.string)}
								<GeoTranslationRow
									string={row.string}
									translation={row.translation}
									is_machine_translation={row.is_machine_translation}
									suggestion={row.suggestion}
									input={row.input}
									on:edit={(e) => editGeoRow(row, e.detail)}
									on:clear={() => clearGeoRow(row)}
									on:machineTranslate={() => machineTranslateGeoRow(row)}
								/>
							{/each}
						</div>
					{/if}
					{#if geoPageCount > 1}
						<div class="geo-pager">
							<IconButton class="material-icons" disabled={geoPage <= 1} on:click={() => setGeoPage(geoPage - 1)}>
								chevron_left
							</IconButton>
							<span>{geoPage} / {geoPageCount}</span>
							<IconButton
								class="material-icons"
								disabled={geoPage >= geoPageCount}
								on:click={() => setGeoPage(geoPage + 1)}
							>
								chevron_right
							</IconButton>
						</div>
					{/if}
				</div>
				<Dialog
					bind:open={mtDialogOpen}
					on:SMUIDialog:closed={onMtDialogClosed}
					aria-labelledby="geo-mt-title"
					aria-describedby="geo-mt-content"
				>
					<DialogTitle id="geo-mt-title">Translate ALL with DeepL</DialogTitle>
					<DialogContent id="geo-mt-content">
						{#if mtState === "confirm"}
							DeepL will check all names that have no translation yet. Only names that actually differ from the
							original are kept, marked 🤖. You can review them via the 🤖 filter.
						{:else if mtState === "running"}
							{#if mtRemaining === null}
								Translating…
							{:else}
								{fmtNumber(mtRemaining)} name{mtRemaining === 1 ? "" : "s"} left · {fmtNumber(mtAdded)}
								translation{mtAdded === 1 ? "" : "s"} added
							{/if}
						{:else if mtState === "error"}
							Machine translation failed. Translations added so far are kept.
						{:else if mtState === "disabled"}
							Machine translation is not available for this language.
						{/if}
					</DialogContent>
					<DialogActions>
						{#if mtState === "confirm"}
							<Button defaultAction>
								<Label>Cancel</Label>
							</Button>
							<Button action={null} on:click={runMtLoop}>
								<Label>Start</Label>
							</Button>
						{:else if mtState === "running"}
							<Button defaultAction>
								<Label>Stop</Label>
							</Button>
						{:else if mtState === "error"}
							<Button defaultAction>
								<Label>Stop</Label>
							</Button>
							<Button action={null} on:click={runMtLoop}>
								<Label>Retry</Label>
							</Button>
						{:else}
							<Button defaultAction>
								<Label>Close</Label>
							</Button>
						{/if}
					</DialogActions>
				</Dialog>
				<Dialog bind:open={copyDialogOpen} aria-labelledby="geo-copy-title" aria-describedby="geo-copy-content">
					<DialogTitle id="geo-copy-title">Copy translations from other graphics</DialogTitle>
					<DialogContent id="geo-copy-content">
						Copy {fmtNumber(geoStatus.suggestions_available)} place name translation{geoStatus.suggestions_available === 1
							? ""
							: "s"} already made on other graphics into this graphic? You can edit or remove them here afterwards.
					</DialogContent>
					<DialogActions>
						<Button defaultAction>
							<Label>Cancel</Label>
						</Button>
						<Button
							action={null}
							on:click={() => {
								copyDialogOpen = false;
								applyGeoSuggestions();
							}}
						>
							<Label>Copy</Label>
						</Button>
					</DialogActions>
				</Dialog>
				<Snackbar bind:this={geoSnackbar}>
					<SnackbarLabel>{geoSnackbarText}</SnackbarLabel>
					<Actions>
						<IconButton class="material-icons" title="Dismiss">close</IconButton>
					</Actions>
				</Snackbar>
			{/if}
			<h5>Embed code</h5>
			{#if graphic.internal_only}
				<div class="embed-container">
					<div class="embed">
						<Textfield
							variant="outlined"
							input$readonly
							input$resizable={false}
							value="Internal graphic — no embed code"
							style="width: 100%;"
							helperLine$style="width: 100%;"
							input$style="height:100%"
							disabled
						/>
					</div>
				</div>
			{:else if oembedMode}
				{#if !hasOembedUrls}
					<div class="embed-container">
						<div class="embed">
							<Textfield
								variant="outlined"
								input$readonly
								input$resizable={false}
								value="No oEmbed-supported embed targets for this project"
								style="width: 100%;"
								helperLine$style="width: 100%;"
								input$style="height:100%"
								disabled
							/>
						</div>
					</div>
				{:else}
					{#if hasMultipleOembedUrls}
						<div class="embed-tabs">
							<TabBar tabs={oembedUrls.map((u) => u.target)} let:tab bind:active={selectedOembedTab}>
								<Tab {tab}>
									<TabLabel>{tab}</TabLabel>
								</Tab>
							</TabBar>
						</div>
					{/if}
					<div class="embed-container">
						<div class="embed">
							<Textfield
								variant="outlined"
								input$readonly
								input$resizable={false}
								value={currentOembedUrl}
								style="width: 100%;"
								helperLine$style="width: 100%;"
								input$style="height:100%"
								on:click={copyOembedUrl}
							/>
						</div>
						<div class="embed-copy">
							<Button on:click={copyOembedUrl} color="primary" variant="raised">
								<Icon class="material-icons button-icon">content_copy</Icon>
								<Label>Copy</Label>
							</Button>
						</div>
						<Snackbar bind:this={snackbar}>
							<SnackbarLabel>Copied URL</SnackbarLabel>
							<Actions>
								<IconButton class="material-icons" title="Dismiss">close</IconButton>
							</Actions>
						</Snackbar>
					</div>
				{/if}
			{:else}
			{#if !hasEmbedCodes}
				<div class="embed-container">
					<div class="embed">
						<Textfield
							variant="outlined"
							input$readonly
							input$resizable={false}
							value="Project not configured to be embedded"
							style="width: 100%;"
							helperLine$style="width: 100%;"
							input$style="height:100%"
							disabled
						/>
					</div>
				</div>
			{:else}
				{#if hasMultipleEmbedCodes}
					<div class="embed-tabs">
						<TabBar tabs={embedCodes.map((code) => code.target)} let:tab bind:active={selectedEmbedTab}>
								<Tab {tab}>
									<TabLabel>{tab}</TabLabel>
								</Tab>
						</TabBar>
					</div>
				{/if}
				<div class="embed-container">
					<div class="embed">
						<Textfield
							variant="outlined"
							input$readonly
							input$resizable={false}
							value={currentEmbedCode}
							style="width: 100%;"
							helperLine$style="width: 100%;"
							input$style="height:100%"
							on:click={copyEmbedCode}
						/>
					</div>
					<div class="embed-copy">
						<Button on:click={copyEmbedCode} color="primary" variant="raised">
							<Icon class="material-icons button-icon">content_copy</Icon>
							<Label>Copy</Label>
						</Button>
					</div>
					<Snackbar bind:this={snackbar}>
						<SnackbarLabel>Copied embed code</SnackbarLabel>
						<Actions>
							<IconButton class="material-icons" title="Dismiss">close</IconButton>
						</Actions>
					</Snackbar>
				</div>
				{#if supportsFixedHeight}
					<div class="fixed-height-option">
						<label class="fixed-height-toggle">
							<input type="checkbox" bind:checked={fixedHeight} />
							Fixed height (no resize script)
						</label>
						{#if fixedHeight}
							<span class="fixed-height-hint">
								Preview at
								<input
									type="number"
									min="200"
									step="50"
									bind:value={fixedHeightPreviewPx}
									class="fixed-height-input"
								/>
								px. Paste into an iframe with a fixed height — the resize script is
								removed and the graphic fills the box.
							</span>
						{/if}
					</div>
				{/if}
			{/if}
			{/if}
			<h5>Use individual themes for this graphic</h5>
			<div class="themes">
				<div class="fonttheme">
					<h6>Font theme</h6>
					<EditFontTheme
						noSelection={true}
						noSelectionLabel={"No overwriting font theme"}
						label={"Select an overwriting font theme"}
						bind:selected={overwriteFontTheme.selected}
						bind:modified={overwriteFontTheme.modified}
						{...overwriteFontTheme}
					/>
				</div>
				{#if $colorthemes.length > 1}
					<div class="colortheme">
						<h6>Color theme</h6>
						<EditColorTheme
							noSelection={true}
							noSelectionLabel={"No overwriting color theme"}
							label={"Select an overwriting color theme"}
							bind:selected={overwriteColorTheme.selected}
							bind:modified={overwriteColorTheme.modified}
							{...overwriteColorTheme}
						/>
					</div>
				{/if}
			</div>
			<div class="delete-container">
				<h5>Delete graphic version</h5>
				Deleting this version of the graphic
				<strong class="secondary">breaks the embed code</strong> and makes it unusable.
				<br />
				You'll have to replace it everywhere you've embedded it.
				<div class="button-container">
					<div>
						Are you sure you want to <strong class="secondary">permanently delete</strong> this version of the graphic?
					</div>
					<Button on:click={() => (instanceToDelete = instance)} color="primary" variant="raised">
						<Icon class="material-icons button-icon">delete</Icon>
						<Label>Delete graphic version</Label>
					</Button>
				</div>
			</div>
			<ButtonSaveFloating
				{modified}
				on:click={() =>
					saveInstance(instance, translations, options, overwriteFontTheme, overwriteColorTheme, geoSavePayload())}
				color="secondary"
			/>
		</div>
	{/if}
</main>

<style lang="scss">
	
	main {
		overflow: scroll;
		padding: 16px;
		height: 100%;
		box-sizing: border-box;
		display: flex;
		justify-content: center;
		width: 100%;
	}

	.container {
		flex-grow: 1;
		max-width: 900px;
	}

	.graphic-instance {
		:global(.mdc-card) {
			padding: 20px;
		}

		:global(.icon) {
			display: inline-block;
			vertical-align: bottom;
		}
	}

	.fixed-height-option {
		margin-top: 10px;
		display: flex;
		flex-direction: column;
		gap: 4px;

		.fixed-height-toggle {
			display: flex;
			align-items: center;
			gap: 8px;
			cursor: pointer;
			font-size: 0.9rem;
		}

		.fixed-height-hint {
			font-size: 0.8rem;
			opacity: 0.7;
		}

		.fixed-height-input {
			width: 5em;
		}
	}

	.embed-container {
		display: flex;
		gap: 30px;
		align-items: center;

		.embed {
			flex: 1 0 300px;

			:global(textarea) {
				font-family: "Roboto Mono", monospace;
			}

			:global(.mdc-text-field span) {
				width: 100%;
				height: 110px;
			}
		}
	}

	.translation-instructions {
		font-size: 0.85em;
		color: rgba(0, 0, 0, 0.6);
		margin-bottom: 15px;

		p {
			margin: 0 0 8px 0;
			&:last-child {
				margin-bottom: 0;
			}
		}

		code {
			background-color: rgba(0, 0, 0, 0.06);
			padding: 2px 6px;
			border-radius: 3px;
			font-family: "Roboto Mono", monospace;
		}
	}

	.translation-info {
		span {
			font-weight: bold;
		}

		.complete {
			color: $success;
		}
		.suggested {
			color: $warning;
		}
		.missing {
			color: $error;
		}
	}

	.translations-container {
		display: grid;
		grid-template-columns: 1fr 1fr;
		column-gap: 15px;
		row-gap: 8px;

		:global(.mdc-text-field--filled) {
			height: 47px;

			&::before {
				height: 36px;
			}
		}
	}

	.translations-section-heading {
		margin-top: 30px;
		margin-bottom: 15px;
		font-size: 0.95em;
		font-weight: 500;
	}

	.long-translations-container {
		display: grid;
		grid-template-columns: 1fr 1fr;
		column-gap: 15px;
		row-gap: 20px;
	}

	.long-translation {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;

		.original-string {
			font-size: 0.7em;
			color: rgba(0, 0, 0, 0.6);
			margin-bottom: 8px;
			line-height: 1.3;
			min-height: 2.6em; // Reserve space for ~2 lines to help align fields
		}

		:global(.mdc-text-field) {
			width: 100%;
		}

		:global(.mdc-text-field--filled) {
			background-color: rgba(0, 0, 0, 0.06);
		}

		:global(textarea) {
			min-height: 80px;
		}

		&.suggestion {
			:global(.mdc-text-field__input) {
				caret-color: $warning !important;
				color: $warning !important;
			}

			:global(.mdc-text-field--filled) {
				background-color: rgba($warning, 0.15) !important;
			}

			:global(.mdc-line-ripple::before),
			:global(.mdc-line-ripple::after) {
				border-bottom-color: $warning !important;
			}

			:global(.mdc-text-field--filled .mdc-text-field__ripple::before),
			:global(.mdc-text-field--filled .mdc-text-field__ripple::after) {
				background-color: $warning !important;
			}
		}

		&.missing {
			:global(.mdc-text-field__input) {
				caret-color: $error !important;
				color: $error !important;
			}

			:global(.mdc-text-field--filled) {
				background-color: rgba($error, 0.15) !important;
			}

			:global(.mdc-line-ripple::before),
			:global(.mdc-line-ripple::after) {
				border-bottom-color: $error !important;
			}

			:global(.mdc-text-field--filled .mdc-text-field__ripple::before),
			:global(.mdc-text-field--filled .mdc-text-field__ripple::after) {
				background-color: $error !important;
			}
		}

		&.too-long {
			$length-warning: #ffc107; // amber-500

			:global(.mdc-text-field__input) {
				caret-color: $length-warning !important;
			}

			:global(.mdc-text-field--filled) {
				background-color: rgba($length-warning, 0.15) !important;
			}

			:global(.mdc-line-ripple::before),
			:global(.mdc-line-ripple::after) {
				border-bottom-color: $length-warning !important;
			}

			:global(.mdc-text-field--filled .mdc-text-field__ripple::before),
			:global(.mdc-text-field--filled .mdc-text-field__ripple::after) {
				background-color: $length-warning !important;
			}

			.length-warning {
				color: $length-warning;
				font-size: 0.75em;
				margin-top: 4px;
			}
		}
	}

	.suggestion-explainer,
	.machine-translation-explainer {
		color: $warning;
	}

	.geo-section {
		.geo-search {
			position: relative;
			margin-bottom: 15px;

			.geo-search-results {
				position: absolute;
				top: 100%;
				left: 0;
				right: 0;
				z-index: 5;
				background: #fff;
				border: 1px solid rgba(0, 0, 0, 0.12);
				border-radius: 0 0 4px 4px;
				box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
				max-height: 320px;
				overflow-y: auto;

				.geo-search-result {
					display: flex;
					justify-content: space-between;
					align-items: baseline;
					gap: 15px;
					width: 100%;
					padding: 10px 16px;
					border: none;
					background: none;
					text-align: left;
					cursor: pointer;
					font: inherit;
					box-sizing: border-box;

					&:hover {
						background-color: rgba(0, 0, 0, 0.04);
					}

					.current {
						font-weight: 500;
					}

					.hint {
						color: rgba(0, 0, 0, 0.6);
						font-size: 0.85em;
					}
				}
			}
		}

		.geo-toolbar {
			display: flex;
			align-items: center;
			gap: 15px;
			flex-wrap: wrap;
			margin-bottom: 15px;

			.geo-count {
				flex: 1;
			}
		}

		.geo-tools-hint {
			margin: 0;
			padding: 0 16px 8px;
			font-size: 0.75em;
			color: rgba(0, 0, 0, 0.6);
		}

		.geo-rows {
			display: grid;
			grid-template-columns: 1fr 1fr;
			column-gap: 15px;
			row-gap: 8px;
		}

		.geo-pager {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 5px;
			margin-top: 10px;
		}
	}

	.delete-container {
		margin-bottom: 100px;

		.button-container {
			display: flex;
			justify-content: space-between;
			gap: 20px;
			flex-wrap: wrap;
			margin-top: 30px;
		}
	}

	h5 {
		margin-bottom: 20px;
	}

	.themes {
		display: flex;
		flex-wrap: wrap;
		gap: 80px;
		width: 100%;

		> div {
			flex: 1 0 300px;

			h6 {
				margin-top: 0px;
			}
		}
	}

	.options-container {
		display: flex;
		flex-direction: column;
		gap: 30px;
	}

	* :global(.content) {
		flex: auto;
		overflow: auto;
		position: relative;
		flex-grow: 1;
	}

	* :global(.mdc-layout-grid__inner) {
		height: 100%;
	}

	iframe {
		width: 100%;
		/*min-height: 550px;*/
    }

  .description {
    margin: 30px 0 50px;
  }

  .darkmode-toggle {
	margin-top: 15px;
	display:flex;
	align-items: center;
	justify-content: end;
	gap: 5px;
  }

  .embed-tabs {
	margin-bottom: 15px;
	
	:global(.mdc-tab-bar) {
		border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	}
	
	:global(.mdc-tab) {
		min-width: 100px;
	}
  }

</style>
